import { useEffect, useState } from "react";

// Reads for the websites page. Each keeps only the fields the page shows and
// falls back to "nothing" on any failure, so a section hides rather than
// breaks.

export type GlobalStats = { contests: number; submissions: number; reviews: number };

export type Activity = { type: string; user: string; action: string; prize?: string; time: string };

export type PublicReview = {
  producer: string;
  track: string;
  reviewer: string;
  feedback: string;
  videoURL?: string;
  audioURL?: string;
};

const str = (v: unknown) => (typeof v === "string" ? v : "");
const count = (v: unknown) => (typeof v === "number" && Number.isFinite(v) && v > 0 ? Math.floor(v) : 0);
// Only http(s) media links reach an href.
const mediaURL = (v: unknown) => (typeof v === "string" && /^https?:\/\//.test(v) ? v : undefined);

export function normalizeStats(raw: unknown): GlobalStats | null {
  if (!raw || typeof raw !== "object") return null;
  const b = raw as Record<string, unknown>;
  const stats = { contests: count(b.contests), submissions: count(b.submissions), reviews: count(b.reviews) };
  return stats.contests || stats.submissions || stats.reviews ? stats : null;
}

export function normalizeActivity(raw: unknown): Activity[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((a): a is Record<string, unknown> => !!a && typeof a === "object")
    .map((a) => ({ type: str(a.type), user: str(a.user), action: str(a.action), prize: str(a.prize) || undefined, time: str(a.time) }))
    .filter((a) => a.user && a.action);
}

export function normalizeReviews(raw: unknown): PublicReview[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((r): r is Record<string, unknown> => !!r && typeof r === "object")
    .map((r) => ({
      producer: str(r.producer_name),
      track: str(r.track_name),
      reviewer: str(r.reviewer_name),
      feedback: str(r.feedback),
      videoURL: mediaURL(r.video_review_url),
      audioURL: mediaURL(r.audio_review_url),
    }))
    .filter((r) => r.reviewer && (r.feedback || r.videoURL || r.audioURL));
}

function useJSON<T>(url: string, normalize: (raw: unknown) => T, empty: T): T {
  const [value, setValue] = useState<T>(empty);
  useEffect(() => {
    const ctrl = new AbortController();
    fetch(url, { signal: ctrl.signal, credentials: "same-origin", headers: { Accept: "application/json" } })
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error(`${url} ${res.status}`))))
      .then((raw) => setValue(normalize(raw)))
      .catch(() => {
        // Nothing to show: the section stays hidden.
      });
    return () => ctrl.abort();
    // normalize and empty are module constants at every call site.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);
  return value;
}

export const useGlobalStats = () => useJSON<GlobalStats | null>("/v1/stats/global", normalizeStats, null);
export const useActivity = () => useJSON<Activity[]>("/v1/activity/feed?limit=6", normalizeActivity, []);
export const usePublicReviews = () => useJSON<PublicReview[]>("/v1/reviews/public?limit=3", normalizeReviews, []);

// 1543 -> "1.5K", 751 -> "751".
export function compactCount(n: number): string {
  if (n < 1000) return String(n);
  const k = n / 1000;
  return `${k >= 10 ? Math.round(k) : Number(k.toFixed(1))}K`;
}
